import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Space,
  Tag,
  Typography,
  Spin,
  Empty,
  Divider,
  Input,
  Radio,
  Timeline,
  message,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import apiService, {
  LeadFileReviewItem,
  LeadFileReviewAudit,
  LeadFileReviewStatus,
} from '../../services/api';
import { formatUtcApiDateTime } from '../../utils/formatDateTime';

const { Text, Title } = Typography;
const { TextArea } = Input;

export const REVIEW_STATUS_COLORS: Record<LeadFileReviewStatus, string> = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
};

export const REVIEW_STATUS_LABELS: Record<LeadFileReviewStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
};

interface FileReviewModalProps {
  open: boolean;
  file: LeadFileReviewItem | null;
  onClose: () => void;
  onReviewed?: (file: LeadFileReviewItem) => void;
}

const FileReviewModal: React.FC<FileReviewModalProps> = ({ open, file, onClose, onReviewed }) => {
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [note, setNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBase64, setPreviewBase64] = useState<string>('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [history, setHistory] = useState<LeadFileReviewAudit[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const cleanupBlob = () => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
    }
  };

  useEffect(() => {
    if (!open || !file) {
      cleanupBlob();
      setPreviewBase64('');
      setPreviewBlobUrl(null);
      setPreviewError(null);
      setHistory([]);
      return;
    }

    setDecision('approve');
    setNote('');
    setRejectionReason('');

    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const fileId = Number(file.id);
        if (!Number.isFinite(fileId)) {
          setPreviewError('ID de ficheiro inválido para pré-visualização.');
          return;
        }
        const resp = await apiService.previewLeadFile(file.lead_id, fileId);
        if (resp.success && 'pdf_data' in resp) {
          const data = resp.pdf_data as string;
          const maxInline = 5 * 1024 * 1024;
          if (data.length > maxInline) {
            const bin = atob(data);
            const arr = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            const blob = new Blob([arr], { type: file.file_type || 'application/pdf' });
            setPreviewBlobUrl(URL.createObjectURL(blob));
          } else {
            setPreviewBase64(data);
          }
        } else if ('error' in resp) {
          setPreviewError(resp.error);
        }
      } catch {
        setPreviewError('Erro ao carregar pré-visualização.');
      } finally {
        setPreviewLoading(false);
      }
    };

    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const fileId = Number(file.id);
        if (!Number.isFinite(fileId)) {
          setHistory([]);
          return;
        }
        const resp = await apiService.getLeadFileReviewHistory(file.lead_id, fileId);
        setHistory(resp.success ? resp.history || [] : []);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadPreview();
    loadHistory();

    return () => {
      cleanupBlob();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.id]);

  const previewMime = useMemo(() => {
    if (!file) return 'application/octet-stream';
    return file.file_type || 'application/pdf';
  }, [file]);

  const isImage = previewMime.startsWith('image/');

  const handleDownload = async () => {
    if (!file) return;
    try {
      const fileId = Number(file.id);
      if (!Number.isFinite(fileId)) {
        message.error('ID de ficheiro inválido para download.');
        return;
      }
      const resp = await apiService.downloadLeadFile(file.lead_id, fileId);
      if (!resp.success) {
        message.error('Erro ao descarregar.');
        return;
      }
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: previewMime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      message.error('Erro ao descarregar.');
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    if (decision === 'reject' && !rejectionReason.trim()) {
      message.error('Indique o motivo da rejeição.');
      return;
    }
    setSubmitting(true);
    try {
      const fileId = Number(file.id);
      if (!Number.isFinite(fileId)) {
        message.error('ID de ficheiro inválido para revisão.');
        return;
      }
      const resp = await apiService.reviewLeadFile(file.lead_id, fileId, {
        action: decision,
        note: note.trim() || undefined,
        rejection_reason: decision === 'reject' ? rejectionReason.trim() : undefined,
      });
      if (resp.success && resp.file) {
        message.success(decision === 'approve' ? 'Ficheiro aprovado.' : 'Ficheiro rejeitado.');
        onReviewed?.(resp.file);
        onClose();
      } else {
        message.error(resp.message || 'Erro ao registar decisão.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={file ? `Revisão — ${file.file_name}` : 'Revisão de Ficheiro'}
      width={960}
      footer={null}
      destroyOnClose
    >
      {!file ? (
        <Empty description="Sem ficheiro selecionado." />
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 480px', minWidth: 320 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space wrap>
                <Tag color={REVIEW_STATUS_COLORS[file.review_status] || 'default'}>
                  {REVIEW_STATUS_LABELS[file.review_status] || file.review_status}
                </Tag>
                {file.is_required ? <Tag color="volcano">Obrigatório</Tag> : null}
                <Text type="secondary">{file.field_name}</Text>
              </Space>
              <Text type="secondary">
                Lead: {file.lead?.name || `#${file.lead_id}`}
                {file.lead?.email ? ` · ${file.lead.email}` : ''}
                {file.lead?.phone ? ` · ${file.lead.phone}` : ''}
              </Text>
              <Text type="secondary">
                Carregado em {formatUtcApiDateTime(file.uploaded_at)}
                {file.user_name ? ` por ${file.user_name}` : ''}
              </Text>

              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  background: '#0f172a',
                  height: 420,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {previewLoading ? (
                  <Spin />
                ) : previewError ? (
                  <Alert message={previewError} type="warning" showIcon style={{ width: '90%' }} />
                ) : previewBlobUrl ? (
                  isImage ? (
                    <img src={previewBlobUrl} alt={file.file_name} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                  ) : (
                    <iframe title="preview" src={previewBlobUrl} width="100%" height="100%" style={{ border: 'none' }} />
                  )
                ) : previewBase64 ? (
                  isImage ? (
                    <img
                      src={`data:${previewMime};base64,${previewBase64}`}
                      alt={file.file_name}
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  ) : (
                    <iframe
                      title="preview"
                      src={`data:${previewMime};base64,${previewBase64}`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                    />
                  )
                ) : (
                  <Empty description={<span style={{ color: '#94a3b8' }}>Sem pré-visualização</span>} />
                )}
              </div>

              <Space>
                <Button icon={<DownloadOutlined />} onClick={handleDownload}>
                  Descarregar
                </Button>
                {previewBlobUrl ? (
                  <Button icon={<EyeOutlined />} href={previewBlobUrl} target="_blank">
                    Abrir em nova aba
                  </Button>
                ) : null}
              </Space>
            </Space>
          </div>

          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <Title level={5} style={{ marginTop: 0 }}>
              Decisão
            </Title>
            <Radio.Group
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              style={{ marginBottom: 12 }}
            >
              <Radio.Button value="approve">
                <CheckCircleOutlined /> Aprovar
              </Radio.Button>
              <Radio.Button value="reject">
                <CloseCircleOutlined /> Rejeitar
              </Radio.Button>
            </Radio.Group>

            {decision === 'reject' ? (
              <TextArea
                placeholder="Motivo da rejeição (obrigatório)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ marginBottom: 12 }}
              />
            ) : null}
            <TextArea
              placeholder="Notas adicionais (opcional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
            />

            <Space style={{ marginTop: 12 }}>
              <Button onClick={onClose}>Cancelar</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                danger={decision === 'reject'}
              >
                Confirmar {decision === 'approve' ? 'Aprovação' : 'Rejeição'}
              </Button>
            </Space>

            <Divider />
            <Title level={5} style={{ marginTop: 0 }}>
              <HistoryOutlined /> Histórico
            </Title>
            {historyLoading ? (
              <Spin />
            ) : history.length === 0 ? (
              <Text type="secondary">Sem decisões anteriores.</Text>
            ) : (
              <Timeline>
                {history.map((entry) => (
                  <Timeline.Item
                    key={entry.id}
                    color={entry.action === 'approved' ? 'green' : 'red'}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {entry.action === 'approved' ? 'Aprovado' : 'Rejeitado'}
                      {entry.reviewer_name ? ` por ${entry.reviewer_name}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {formatUtcApiDateTime(entry.created_at)}
                    </div>
                    {entry.rejection_reason ? (
                      <div style={{ marginTop: 4 }}>
                        <Text type="danger">Motivo:</Text> {entry.rejection_reason}
                      </div>
                    ) : null}
                    {entry.note ? (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">Nota:</Text> {entry.note}
                      </div>
                    ) : null}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default FileReviewModal;
